import { useEditorHistoryContext } from "@babackapp/blocks-editor/contexts/editor-history-context";
import { useTranslateTemplate } from "@babackapp/private-api-client/client/use-translate-template";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import { ContextualTipsTrigger } from "@babackapp/ui/components/contextual-tips";
import { Dialog, DialogContent, DialogTrigger } from "@babackapp/ui/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import { ScrollArea } from "@babackapp/ui/components/scroll-area";
import { cn } from "@babackapp/ui/lib/utils";
import { useTranslation } from "react-i18next";
import { ArrowRight, CheckIcon, LoaderIcon, XCircleIcon } from "lucide-react";
import { AnimatedWandSparkles } from "./animated-wand-sparkles";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useEmailEditorLocale } from "../context/templates-editor-locale-context";
import useCurrentShopSuspense from "../hooks/use-current-shop-suspense";
import { EmailTemplateFormData } from "../types";
import { LocaleStateIcon } from "./locale-state-icon";


type TranslationStatus = "pending" | "in_progress" | "completed" | "error";


interface LocaleTranslationState {
  locale: string;
  status: TranslationStatus;
  error?: string;
}


interface LocaleInfo {
  value: string;
  label: string;
  flag: string;
  state: string;
}


const PARALLEL_TRANSLATIONS = 2;


function TranslationStatusIcon({ status }: { status: TranslationStatus }) {
  switch (status) {
    case "completed":
      return <CheckIcon className="size-3.5 text-green-500" />;
    case "in_progress":
      return <LoaderIcon className="size-3.5 animate-spin text-primary" />;
    case "error":
      return <XCircleIcon className="size-3.5 text-destructive" />;
    case "pending":
    default:
      return <LoaderIcon className="size-3.5 text-muted-foreground" />;
  }
}


function LocaleTag({
  locale,
  isSelected,
  onToggle,
  status,
  disabled,
}: {
  locale: LocaleInfo;
  isSelected: boolean;
  onToggle: () => void;
  status?: TranslationStatus;
  disabled?: boolean;
}) {
  const isShowingStatus = status !== undefined;

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled || isShowingStatus}
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-sm transition-colors",
        !isShowingStatus && "cursor-pointer hover:bg-accent",
        isSelected && !isShowingStatus && "border-primary bg-primary/5",
        disabled && "cursor-default",
      )}
    >
      {isShowingStatus ? (
        <TranslationStatusIcon status={status} />
      ) : (
        <div
          className={cn(
            "size-3.5 rounded-full border-2 flex items-center justify-center transition-colors",
            isSelected ? "border-primary" : "border-muted-foreground",
          )}
        >
          {isSelected && <div className="size-2 rounded-full bg-primary" />}
        </div>
      )}
      <span>{locale.label}</span>
      <span>{locale.flag}</span>
    </button>
  );
}


function TranslateLocaleSwitcher({
  value,
  onChange,
}: {
  value: LocaleInfo;
  onChange: (value: string) => void;
}) {
  const { availableLocales } = useEmailEditorLocale();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Badge variant="outline">
          {value.label} {value.flag}
        </Badge>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <ScrollArea className="max-h-56 overflow-y-auto">
          {availableLocales.map(({ value, label, flag, state }) => (
            <DropdownMenuItem key={value} onSelect={() => onChange(value)}>
              {label} {flag} <LocaleStateIcon state={state} />
            </DropdownMenuItem>
          ))}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


interface TranslateTemplateDialogContentProps {
  locale: string;
  onClose: () => void;
  onTranslatingChange: (isTranslating: boolean) => void;
}


function TranslateTemplateDialogContent({
  locale,
  onClose,
  onTranslatingChange,
}: TranslateTemplateDialogContentProps) {
  const { data: shop } = useCurrentShopSuspense();
  const { t } = useTranslation("trackoffice");
  const translateTemplate = useTranslateTemplate();
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationStates, setTranslationStates] = useState<LocaleTranslationState[]>([]);
  const { setCurrentLocale, availableLocales, defaultLocale } = useEmailEditorLocale();
  const form = useFormContext<EmailTemplateFormData>();
  const { captureState } = useEditorHistoryContext();

  const [sourceLocale, setSourceLocale] = useState(defaultLocale);
  const [targetLocales, setTargetLocales] = useState([locale]);

  const getLocale = (loc: string): LocaleInfo => {
    return (
      availableLocales.find((l) => l.value === loc) ?? {
        value: loc,
        label: loc.toUpperCase(),
        flag: "",
        state: "empty",
      }
    );
  };

  // Remove source locale from target locales if it's included
  useEffect(() => {
    setTargetLocales((prev) => prev.filter((target) => target !== sourceLocale));
  }, [sourceLocale]);

  // Initialize translation states when target locales change
  useEffect(() => {
    setTranslationStates(
      targetLocales.map((loc) => ({ locale: loc, status: "pending" })),
    );
  }, [targetLocales]);

  // Notify parent when translation state change
  useEffect(() => {
    onTranslatingChange(isTranslating);
  }, [isTranslating, onTranslatingChange]);

  const updateLocaleStatus = (
    loc: string,
    status: TranslationStatus,
    error?: string,
  ) => {
    setTranslationStates((prev) =>
      prev.map((state) =>
        state.locale === loc ? { ...state, status, error } : state,
      ),
    );
  };

  const translateSingleLocale = async (targetLocale: string) => {
    updateLocaleStatus(targetLocale, "in_progress");

    try {
      const { translated } = await translateTemplate.mutateAsync({
        targetLocales: [targetLocale],
        sourceLocale: sourceLocale,
        input: form.getValues(),
        shopId: shop.id,
      });

      Object.entries(translated).forEach(([key, value]) =>
        form.setValue(key as Parameters<typeof form.setValue>[0], value, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        }),
      );

      updateLocaleStatus(targetLocale, "completed");
      return true;
    } catch {
      updateLocaleStatus(targetLocale, "error", "Translation failed");
      return false;
    }
  };

  const onTranslate = async () => {
    if (targetLocales.length === 0) return;

    setIsTranslating(true);

    // Pool-based approach: as soon as one translation finishes, start the next
    let index = 0;
    const processNext = async (): Promise<void> => {
      if (index >= targetLocales.length) return;
      const currentLocale = targetLocales[index++];
      await translateSingleLocale(currentLocale);
      await processNext();
    };

    await Promise.all(
      Array.from(
        { length: Math.min(PARALLEL_TRANSLATIONS, targetLocales.length) },
        processNext,
      ),
    );

    setTimeout(() => captureState(), 0);

    setIsTranslating(false);
    setCurrentLocale(targetLocales[0]);
  };

  const hasCompletedAll =
    translationStates.length > 0 &&
    translationStates.every((s) => s.status === "completed");
  const hasErrors = translationStates.some((s) => s.status === "error");
  const hasStartedTranslation = isTranslating || hasCompletedAll || hasErrors;

  const availableTargetLocales = availableLocales.filter(
    (l) => l.value !== sourceLocale,
  );

  const localesToDisplay = hasStartedTranslation
    ? availableTargetLocales.filter((l) => targetLocales.includes(l.value))
    : availableTargetLocales;

  const areAllLocalesSelected =
    availableTargetLocales.length > 0 &&
    availableTargetLocales.every((l) => targetLocales.includes(l.value));

  const toggleAllLocales = () => {
    if (areAllLocalesSelected) {
      setTargetLocales([]);
    } else {
      setTargetLocales(availableTargetLocales.map((l) => l.value));
    }
  };

  const toggleLocale = (localeValue: string) => {
    if (targetLocales.includes(localeValue)) {
      setTargetLocales(targetLocales.filter((l) => l !== localeValue));
    } else {
      setTargetLocales([...targetLocales, localeValue]);
    }
  };

  const getStatusForLocale = (localeValue: string): TranslationStatus | undefined => {
    if (!hasStartedTranslation) return undefined;
    const state = translationStates.find((s) => s.locale === localeValue);
    return state?.status;
  };

  return (
    <div className="flex flex-col items-center justify-center gap-4 text-center">
      <div className="bg-muted size-24 rounded-full flex items-center justify-center">
        {hasCompletedAll ? (
          <CheckIcon className="size-10 text-green-500" />
        ) : (
          <AnimatedWandSparkles className="size-10 text-primary" isAnimating={isTranslating} />
        )}
      </div>

      <div className="flex flex-col gap-6 mt-2">
        <span className="text-semibold text-md">
          {hasStartedTranslation
            ? hasCompletedAll
              ? t("templatesEditor.translate.completed", "Translation completed")
              : t("templatesEditor.translate.inProgress", "Translating...")
            : (
              <>
                {t(
                  "templatesEditor.translate.autoTranslateFrom",
                  "Auto translate from",
                )}{" "}
                <TranslateLocaleSwitcher
                  value={getLocale(sourceLocale)}
                  onChange={setSourceLocale}
                />{" "}
                {t("templatesEditor.translate.to", "to")}
              </>
            )}
        </span>

        <div className="flex flex-wrap gap-2 justify-center max-w-md">
          <AnimatePresence mode="popLayout">
            {!hasStartedTranslation && (
              <motion.div
                key="select-all"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                layout
              >
                <button
                  type="button"
                  onClick={toggleAllLocales}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-sm transition-colors",
                    "cursor-pointer hover:bg-accent border-dashed",
                    areAllLocalesSelected && "border-primary bg-primary/5",
                  )}
                >
                  <div
                    className={cn(
                      "size-3.5 rounded-full border-2 flex items-center justify-center transition-colors",
                      areAllLocalesSelected ? "border-primary" : "border-muted-foreground",
                    )}
                  >
                    {areAllLocalesSelected && <div className="size-2 rounded-full bg-primary" />}
                  </div>
                  <span>
                    {areAllLocalesSelected
                      ? t("templatesEditor.translate.deselectAll", "Deselect all")
                      : t("templatesEditor.translate.selectAll", "Select all")}
                  </span>
                </button>
              </motion.div>
            )}
            {localesToDisplay.map((locale) => {
              const isSelected = targetLocales.includes(locale.value);
              const status = getStatusForLocale(locale.value);

              return (
                <motion.div
                  key={locale.value}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                  layout
                >
                  <LocaleTag
                    locale={locale}
                    isSelected={isSelected}
                    onToggle={() => toggleLocale(locale.value)}
                    status={status}
                    disabled={isTranslating}
                  />
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      {!hasStartedTranslation && (
        <p className="text-sm text-muted-foreground max-w-sm">
          {t(
            "templatesEditor.translate.disclaimer",
            "We use LLMs to translate the email content. AI can make mistakes. Please review the translated content before sending it.",
          )}
        </p>
      )}

      {!hasStartedTranslation ? (
        <Button
          disabled={isTranslating || targetLocales.length === 0}
          size="sm"
          variant="default"
          onClick={onTranslate}
        >
          {t("templatesEditor.translate.button", "Translate")} <ArrowRight />
        </Button>
      ) : (hasCompletedAll || hasErrors) && (
        <Button
          size="sm"
          variant={hasErrors ? "outline" : "default"}
          onClick={onClose}
        >
          {hasErrors
            ? t("templatesEditor.translate.close", "Close")
            : t("templatesEditor.translate.done", "Done")}
        </Button>
      )}
    </div>
  );
}


export function TranslateTemplateDialog({ locale }: { locale: string }) {
  const { t } = useTranslation("trackoffice");
  const [open, setOpen] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  const handleOpenChange = (newOpen: boolean) => {
    // Prevent closing while translation is in progress
    if (!newOpen && isTranslating) return;
    setOpen(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <ContextualTipsTrigger
        wrapped
        wrapperClassName="flex"
        tips={[
          {
            id: "template-magic-i18n",
            title: t(
              "templatesEditor.tips.templateI18nLLM.title",
              "Use our AI to translate your template",
            ),
            description: t(
              "templatesEditor.tips.templateI18nLLM.description",
              "You can automatically translate your template to any language. Simply select the languages you want to translate to, our AI will do the rest.",
            ),
          },
        ]}
      >
        <DialogTrigger asChild>
          <button className="text-muted-foreground hover:text-foreground transition-colors border-l pl-2">
            <AnimatedWandSparkles className="size-3" />
          </button>
        </DialogTrigger>
      </ContextualTipsTrigger>
      <DialogContent hideCloseButton={isTranslating}>
        {open && (
          <TranslateTemplateDialogContent
            locale={locale}
            onClose={() => setOpen(false)}
            onTranslatingChange={setIsTranslating}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
